import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Download, RefreshCw, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "devkii_admin_token";

const inputClass =
  "w-full border border-ink/15 bg-transparent px-4 py-3.5 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-ink/35 focus:border-brand";

const labelClass = "mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-ink/50";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const toCsv = (rows) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = ["Name", "Company", "Email", "Phone", "Subject", "Message", "Received"];
  const lines = rows.map((r) =>
    [r.name, r.company, r.email, r.phone, r.subject, r.message, formatDate(r.created_at)].map(esc).join(",")
  );
  return [header.map(esc).join(","), ...lines].join("\n");
};

export const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState("");

  const loadEnquiries = useCallback(
    async (t) => {
      setLoading(true);
      setFetchError("");
      try {
        const headers = { Authorization: `Bearer ${t}` };
        const [enqRes, dlRes] = await Promise.all([
          fetch(`${API}/enquiries`, { headers }),
          fetch(`${API}/admin/catalogue-downloads`, { headers }),
        ]);
        if (enqRes.status === 401 || enqRes.status === 403 || dlRes.status === 401 || dlRes.status === 403) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken("");
          return;
        }
        if (!enqRes.ok || !dlRes.ok) throw new Error();
        setEnquiries(await enqRes.json());
        setDownloads(await dlRes.json());
      } catch {
        setFetchError("Could not load enquiries. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) loadEnquiries(token);
  }, [token, loadEnquiries]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        setLoginError(res.status === 401 ? "Invalid email or password." : "Login failed. Please try again.");
        return;
      }
      const data = await res.json();
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch {
      setLoginError("Login failed. Please try again.");
    } finally {
      setLoggingIn(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries([]);
    setDownloads([]);
  };

  const handleExport = () => {
    const blob = new Blob([toCsv(enquiries)], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `devkii-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main data-testid="admin-page" className="min-h-screen bg-paper text-ink">
      <div className="mx-auto w-full max-w-[1200px] px-6 pb-24 pt-32 sm:px-10 lg:px-16 lg:pt-40">
        {!token ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto max-w-md"
          >
            <p className="mb-4 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-brand">
              <span className="inline-block h-2 w-2 bg-brand" />
              Admin
            </p>
            <h1 className="font-heading text-4xl font-black tracking-tighter" data-testid="admin-login-heading">
              Enquiry Dashboard
            </h1>
            <p className="mt-3 text-sm text-ink/55">Sign in to view and export website enquiries.</p>
            <form onSubmit={handleLogin} className="mt-8 space-y-5 border border-ink/10 bg-white p-8" data-testid="admin-login-form">
              <div>
                <label htmlFor="admin-email" className={labelClass}>Email</label>
                <input id="admin-email" data-testid="admin-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} placeholder="admin email" />
              </div>
              <div>
                <label htmlFor="admin-password" className={labelClass}>Password</label>
                <input id="admin-password" data-testid="admin-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} placeholder="password" />
              </div>
              {loginError && (
                <p className="text-sm text-brand" data-testid="admin-login-error">{loginError}</p>
              )}
              <button
                type="submit"
                disabled={loggingIn}
                data-testid="admin-login-submit"
                className="w-full bg-brand px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-ink disabled:opacity-60"
              >
                {loggingIn ? "Signing in…" : "Sign In"}
              </button>
            </form>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
            <div className="flex flex-wrap items-end justify-between gap-6 border-b border-ink/10 pb-8">
              <div>
                <p className="mb-4 flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.35em] text-brand">
                  <span className="inline-block h-2 w-2 bg-brand" />
                  Admin
                </p>
                <h1 className="font-heading text-4xl font-black tracking-tighter sm:text-5xl" data-testid="admin-heading">
                  Enquiries
                </h1>
                <p className="mt-2 text-sm text-ink/50" data-testid="admin-count">
                  {enquiries.length} received
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => loadEnquiries(token)}
                  data-testid="admin-refresh"
                  className="inline-flex items-center gap-2 border border-ink/20 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 hover:border-brand hover:text-brand"
                >
                  <RefreshCw size={14} /> Refresh
                </button>
                <button
                  onClick={handleExport}
                  disabled={enquiries.length === 0}
                  data-testid="admin-export"
                  className="inline-flex items-center gap-2 bg-brand px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-ink disabled:opacity-50"
                >
                  <Download size={14} /> Export CSV
                </button>
                <button
                  onClick={handleLogout}
                  data-testid="admin-logout"
                  className="inline-flex items-center gap-2 border border-ink/20 px-5 py-3 text-[12px] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 hover:border-brand hover:text-brand"
                >
                  <LogOut size={14} /> Logout
                </button>
              </div>
            </div>

            {fetchError && <p className="mt-6 text-sm text-brand" data-testid="admin-fetch-error">{fetchError}</p>}
            {loading && <p className="mt-10 text-sm uppercase tracking-[0.25em] text-ink/40" data-testid="admin-loading">Loading…</p>}

            {!loading && !fetchError && enquiries.length === 0 && (
              <p className="mt-16 text-center text-sm uppercase tracking-[0.25em] text-ink/40" data-testid="admin-empty">
                No enquiries yet
              </p>
            )}

            <div className="divide-y divide-ink/10" data-testid="enquiry-list">
              {enquiries.map((enq, i) => (
                <article key={i} className="py-7" data-testid={`enquiry-row-${i}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h2 className="font-heading text-lg font-bold tracking-tight">{enq.name}</h2>
                    <span className="text-xs uppercase tracking-[0.15em] text-ink/40">{formatDate(enq.created_at)}</span>
                  </div>
                  <p className="mt-1.5 text-sm text-ink/60">
                    <a href={`mailto:${enq.email}`} className="transition-colors hover:text-brand">{enq.email}</a>
                    {" · "}
                    <a href={`tel:${enq.phone.replace(/\s/g, "")}`} className="transition-colors hover:text-brand">{enq.phone}</a>
                    {enq.company && <span className="text-ink/40">{" · "}{enq.company}</span>}
                  </p>
                  {enq.subject && (
                    <p className="mt-3 inline-block border-l-2 border-brand pl-3 text-[12px] font-semibold uppercase tracking-[0.18em] text-ink/70">
                      {enq.subject}
                    </p>
                  )}
                  <p className="mt-3 max-w-3xl text-sm leading-relaxed text-ink/60">{enq.message}</p>
                </article>
              ))}
            </div>

            <div className="mt-16 border-t border-ink/10 pt-10" data-testid="downloads-section">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="font-heading text-2xl font-black tracking-tight" data-testid="downloads-heading">
                  Catalogue Downloads
                </h2>
                <p className="text-sm text-ink/50" data-testid="downloads-count">
                  {downloads.length} verified number{downloads.length === 1 ? "" : "s"} ·{" "}
                  {downloads.reduce((sum, d) => sum + (d.download_count || 0), 0)} download{downloads.reduce((sum, d) => sum + (d.download_count || 0), 0) === 1 ? "" : "s"}
                </p>
              </div>

              {downloads.length === 0 ? (
                <p className="mt-8 text-sm uppercase tracking-[0.25em] text-ink/40" data-testid="downloads-empty">
                  No verified downloads yet
                </p>
              ) : (
                <div className="mt-6 divide-y divide-ink/10" data-testid="downloads-list">
                  {downloads.map((d, i) => (
                    <div key={i} className="flex flex-wrap items-baseline justify-between gap-2 py-5" data-testid={`download-row-${i}`}>
                      <div>
                        <a href={`tel:${d.phone}`} className="font-heading text-base font-bold tracking-tight transition-colors hover:text-brand">
                          {d.phone}
                        </a>
                        <p className="mt-1 text-xs uppercase tracking-[0.15em] text-ink/40">
                          Verified {formatDate(d.verified_at)}
                          {d.verifications > 1 && ` · ${d.verifications} verifications`}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="font-heading text-base font-bold tracking-tight">{d.download_count || 0}</span>
                        <span className="ml-1 text-xs uppercase tracking-[0.15em] text-ink/40">downloads</span>
                        {d.last_download_at && (
                          <p className="mt-1 text-xs uppercase tracking-[0.15em] text-ink/40">
                            Last {formatDate(d.last_download_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </main>
  );
};
